import * as React from 'react'
// libs
import styled from 'styled-components'
import { InjectedFormProps, reduxForm } from 'redux-form'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
import { SecurityProfilesContext } from 'containers/Security/components/SecurityProfiles/layers/API'
// constants
import { Forms } from 'constants/index'
import { LICENCES_NAMES } from 'constants/licences'
import {
  ACTION_FUNCTIONS,
  ADMINISTRATION_FUNCTIONS,
  AI_FUNCTIONS,
  DATA_HUB_FUNCTIONS,
  FORM_FUNCTIONS,
  GENERAL_FUNCTIONS,
  KPI_FUNCTIONS,
  OBJECTIVE_FUNCTIONS,
  PLAN_FUNCTIONS
} from 'constants/security'
// components
import DeleteButton from 'components/Modals/GroupSecurityModal/Form/BusinessRule/components/DeleteButton'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxInput, ReduxSwitch } from 'components/FormElements/ReduxForm'
// hooks
import { useCompanySubscriptionLevel, useCurrentUser } from 'hooks/index'
// types
import { CompanySubscriptionLevel, ISecurityProfile } from 'types/graphql'
// view components
import ProfileFunctions from './components/ProfileFunctions'
// views
import { BottomPaper, Form, Scrollbar as ScrollbarOrigin } from 'themes/views/security'
// utils
import get from 'lodash/get'
import { required } from 'utils/index'

const Scrollbar = styled(ScrollbarOrigin)`
  > div {
    margin-bottom: 0;
  }

  > div:nth-child(2) {
    margin: 16px 0;
  }
`

interface IIncomingProps {
  hideModal: () => void
  isEdit: boolean
  profile: ISecurityProfile
}

const SecurityProfileForm: React.FC<InjectedFormProps & IIncomingProps> = ({
  isEdit,
  handleSubmit,
  hideModal,
  profile,
  submitting
}) => {
  const user = useCurrentUser()

  const { filter } = React.useContext(TableManagementContext)
  const { deleteSecurityProfile } = React.useContext(SecurityProfilesContext)

  const canSeeFormSection = get(
    get(user.company, 'licences', []).find(({ name }) => name === LICENCES_NAMES.FORMS),
    'active',
    false
  )

  const generalFunctions = GENERAL_FUNCTIONS.filter(
    ({ name }) => !(!canSeeFormSection && name === 'manageRiskTemplates')
  )

  const administrationFunctions = ADMINISTRATION_FUNCTIONS.filter(
    ({ name }) => !(!canSeeFormSection && name === 'adminViewPrivateRisks')
  )

  const companySubscriptionLevel = useCompanySubscriptionLevel(filter.companyId)

  const handleDeleteSecurityProfile = () => deleteSecurityProfile(profile.id, hideModal)

  return (
    <Form onSubmit={handleSubmit} data-testid='security-profile-form'>
      <Scrollbar style={{ paddingRight: '24px' }}>
        {isEdit && <DeleteButton iconSize={24} handleOnClick={handleDeleteSecurityProfile} />}
        <ReduxSwitch name={'active'} label={'Active'} reverse />
        <ReduxInput name={'name'} label={'Name'} validate={[required]} />
        <ProfileFunctions
          isLocked={companySubscriptionLevel === CompanySubscriptionLevel.Standard}
          label={'General'}
          options={generalFunctions}
        />
        <ProfileFunctions label={'AI'} options={AI_FUNCTIONS} />
        <ProfileFunctions label={'Plan'} options={PLAN_FUNCTIONS} />
        <ProfileFunctions label={'Objective'} options={OBJECTIVE_FUNCTIONS} />
        <ProfileFunctions label={'Action'} options={ACTION_FUNCTIONS} />
        <ProfileFunctions label={'Indicator'} options={KPI_FUNCTIONS} />
        {canSeeFormSection && <ProfileFunctions label={'Form'} options={FORM_FUNCTIONS} />}
        <ProfileFunctions
          isLocked={companySubscriptionLevel !== CompanySubscriptionLevel.Enterprise}
          isSkeftoAdmin={user.isAdmin}
          label={'the Data Hub'}
          options={DATA_HUB_FUNCTIONS}
        />
        <ProfileFunctions label={'Administration'} options={administrationFunctions} />
      </Scrollbar>

      <BottomPaper square>
        <Button type={'submit'} layout={BUTTON_LAYOUTS.PURPLE} isLoading={submitting}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button layout={BUTTON_LAYOUTS.WHITE} type={'button'} onClick={hideModal}>
          Cancel
        </Button>
      </BottomPaper>
    </Form>
  )
}

export default reduxForm<any, any>({
  form: Forms.PROFILE_CREATE_FORM,
  enableReinitialize: true
})(SecurityProfileForm)
